/**
 * Canonical attribution labels.
 *
 * Attribution_Type__c in Salesforce is a picklist. Every value the pixel puts
 * there must be one of the TDG_* labels below (or Osano_Blocked / an explicit
 * override). Raw UTM detail is passed through separately in the UTM_* fields,
 * so the attribution label only needs to answer "which channel".
 *
 * Used by both the detector (live URL) and the injector's stored-UTM fallback
 * so the two paths can never disagree.
 */

import { sanitizeSource } from './utils';

export const Sources = {
  google: 'TDG_Google',
  googleOrganic: 'TDG_Google_Organic',
  facebook: 'TDG_Facebook',
  instagram: 'TDG_Instagram',
  linkedin: 'TDG_LinkedIn',
  twitter: 'TDG_Twitter',
  tiktok: 'TDG_TikTok',
  youtube: 'TDG_YouTube',
  bing: 'TDG_Bing',
  yahoo: 'TDG_Yahoo',
  duckduckgo: 'TDG_DuckDuckGo',
  email: 'TDG_Email',
  paidSocial: 'TDG_Paid_Social',
  campaign: 'TDG_Campaign',
  referral: 'TDG_Referral',
  direct: 'TDG_Direct',
} as const;

/**
 * Platform click identifiers. Checked BEFORE UTMs because they are appended
 * by the ad platform itself and are the most reliable signal of origin.
 * Order matters only for the (rare) case of multiple IDs on one URL.
 */
const CLICK_ID_MAP: ReadonlyArray<[string, string]> = [
  ['gclid', Sources.google],
  ['gbraid', Sources.google],
  ['wbraid', Sources.google],
  ['fbclid', Sources.facebook],
  ['igshid', Sources.instagram],
  ['igsh', Sources.instagram],
  ['li_fat_id', Sources.linkedin],
  ['ttclid', Sources.tiktok],
  ['twclid', Sources.twitter],
  ['msclkid', Sources.bing],
];

export function sourceFromClickId(params: URLSearchParams): string | null {
  for (const [key, label] of CLICK_ID_MAP) {
    if (params.get(key)) return label;
  }
  return null;
}

/**
 * utm_source → channel. Matched against a lowercased source.
 */
const UTM_SOURCE_MAP: ReadonlyArray<[RegExp, string]> = [
  [/^(facebook|fb|meta)$|facebook/, Sources.facebook],
  [/^(instagram|ig)$|instagram/, Sources.instagram],
  [/linkedin/, Sources.linkedin],
  [/^(twitter|x)$|twitter/, Sources.twitter],
  [/tiktok/, Sources.tiktok],
  [/youtube/, Sources.youtube],
  [/google/, Sources.google],
  [/^(bing|microsoft|msn)$|bing/, Sources.bing],
  [/yahoo/, Sources.yahoo],
  [/duckduckgo/, Sources.duckduckgo],
  [/^(email|sfmc|newsletter|mail)$|email|sfmc/, Sources.email],
];

/**
 * Map a utm_source / utm_medium pair to a canonical label.
 * Returns null when neither is present.
 *
 *   utm_source=linkedin                     → TDG_LinkedIn
 *   utm_source=facebook&utm_medium=cpc      → TDG_Facebook
 *   utm_source=tdg&utm_medium=paidsocial    → TDG_Paid_Social (source not
 *                                             platform-specific; medium is)
 *   utm_source=email&utm_medium=newsletter  → TDG_Email
 *   utm_source=partner_xyz                  → TDG_Campaign
 */
export function sourceFromUtms(
  source: string | null | undefined,
  medium: string | null | undefined
): string | null {
  const s = (source ?? '').trim().toLowerCase();
  const m = (medium ?? '').trim().toLowerCase();
  if (!s && !m) return null;

  if (s) {
    for (const [pattern, label] of UTM_SOURCE_MAP) {
      if (pattern.test(s)) {
        // Google with a paid medium is paid; otherwise treat as organic-ish
        if (label === Sources.google && m && !/cpc|ppc|paid|ads?/.test(m)) {
          return Sources.googleOrganic;
        }
        return label;
      }
    }
  }

  // Source didn't name a platform — fall back on the medium.
  if (/paid[_\-\s]?social|social/.test(m)) return Sources.paidSocial;
  if (/e?mail|newsletter/.test(m)) return Sources.email;

  return Sources.campaign;
}

/** Max length accepted by journey.addTouchpoint's isSafeString check. */
const MAX_LABEL_LEN = 100;

export interface UtmDetail {
  source?: string | null;
  medium?: string | null;
  campaign?: string | null;
  content?: string | null;
  term?: string | null;
  id?: string | null;
}

/**
 * Append UTM detail to a canonical label, per config.attributionDetail.
 *
 *   'label'    → label unchanged
 *   'campaign' → label + _ + utm_campaign
 *   'full'     → label + _ + source_medium_campaign_content_term_id
 *
 * Values are sanitized (lowercase, [a-z0-9_-]) and the result is capped at
 * 100 chars so it always passes the journey's safety check.
 */
export function withUtmDetail(
  label: string,
  utms: UtmDetail | null | undefined,
  detail: 'label' | 'campaign' | 'full'
): string {
  if (detail === 'label' || !utms) return label;
  const parts =
    detail === 'campaign'
      ? [utms.campaign]
      : [utms.source, utms.medium, utms.campaign, utms.content, utms.term, utms.id];
  const suffix = parts
    .filter((v): v is string => !!v)
    .map(sanitizeSource)
    .filter(Boolean)
    .join('_');
  if (!suffix) return label;
  return (label + '_' + suffix).slice(0, MAX_LABEL_LEN).replace(/_$/, '');
}

/**
 * document.referrer → channel. Includes the `android-app://` referrers that
 * Chrome reports for in-app browsers, and the link-shortener domains the
 * platforms bounce outbound clicks through.
 */
const REFERRER_MAP: ReadonlyArray<[RegExp, string]> = [
  [/facebook\.com|fb\.com|fb\.me|com\.facebook\./, Sources.facebook],
  [/instagram\.com|com\.instagram\./, Sources.instagram],
  [/twitter\.com|(^|\/\/|\.)x\.com|t\.co\/|com\.twitter\./, Sources.twitter],
  [/linkedin\.com|lnkd\.in|com\.linkedin\./, Sources.linkedin],
  [/tiktok\.com|com\.zhiliaoapp\.|com\.ss\.android/, Sources.tiktok],
  [/youtube\.com|youtu\.be|com\.google\.android\.youtube/, Sources.youtube],
  [/google\./, Sources.googleOrganic],
  [/bing\.|msn\./, Sources.bing],
  [/yahoo\./, Sources.yahoo],
  [/duckduckgo\./, Sources.duckduckgo],
];

export function sourceFromReferrer(ref: string): string | null {
  if (!ref) return null;
  const lower = ref.toLowerCase();
  for (const [pattern, label] of REFERRER_MAP) {
    if (pattern.test(lower)) return label;
  }
  return Sources.referral;
}
